import * as React from "react"
import { createPortal } from "react-dom"
import { Data } from "rete/types/core/data"
import { debounce } from "lodash"
import {
  buildCategoricalDetailMatrix,
  categoryGroupKeyForLabel,
  getCategoricalToNumericDetailEntries,
  chartDetailIcon,
  chartDetailKindLabel,
  chartEntryHasData,
  getChartDetailEntries,
  getChartDetailEntry,
  getNumericConstantDetailEntries,
  getPayoffMatrixDetailEntries,
  getTableDetailEntries,
  getTableDetailEntry,
  payoffMatrixCellValueString,
  readDetailGroupByCategory,
  tableCellValueString,
  writeDetailGroupByCategory,
  type CategoricalDetailComponentRow,
  type CategoricalDetailMatrix,
  type CategoricalMapDetailRow,
  type CategoricalToNumericDetailEntry,
  type ChartDetailEntry,
  type NumericConstantDetailEntry,
  type PayoffMatrixDetailEntry,
  type TableDetailEntry,
} from "./model_detail"
import type { ModelEditorApi } from "./model_editor_api"
import { ChartDetailBody } from "./chart_detail_body"
import {
  downloadCategoricalDetailMatrixCsv,
  parseCategoricalDetailMatrixCsv,
} from "../snapshot/categorical_detail_matrix_csv"
import {
  downloadPayoffMatrixCsv,
  parsePayoffMatrixCsv,
} from "../modelling/payoff_matrix_csv"
import {
  downloadTableSpreadsheetCsv,
  downloadTableSpreadsheetXlsx,
  parseTableSpreadsheetCsv,
  parseTableSpreadsheetXlsx,
  type TableSpreadsheetData,
} from "../modelling/table_spreadsheet"
import type { PayoffAxesConfig } from "../modelling/payoff_matrix_logic"
import { useExpandedChartId } from "../snapshot/chart_detail_expand"

type ModelDetailPanelProps = {
  model: Data | null
  modelEditorApi: ModelEditorApi | null
  highlightedCategoryGroupKey?: string | null
}

export const ModelDetailPanel = ({
  model,
  modelEditorApi,
  highlightedCategoryGroupKey = null,
}: ModelDetailPanelProps) => {
  const [groupByCategory, setGroupByCategory] = React.useState(readDetailGroupByCategory)
  const numericEntries = React.useMemo(() => getNumericConstantDetailEntries(model), [model])
  const categoricalEntries = React.useMemo(() => getCategoricalToNumericDetailEntries(model), [model])
  const payoffEntries = React.useMemo(() => getPayoffMatrixDetailEntries(model), [model])
  const tableEntries = React.useMemo(() => getTableDetailEntries(model), [model])
  const chartEntries = React.useMemo(() => getChartDetailEntries(model), [model])
  const categoryMatrix = React.useMemo(
    () => buildCategoricalDetailMatrix(categoricalEntries),
    [categoricalEntries]
  )
  const hasParameterEntries =
    numericEntries.length > 0 ||
    categoricalEntries.length > 0 ||
    payoffEntries.length > 0 ||
    tableEntries.length > 0
  const hasEntries = hasParameterEntries || chartEntries.length > 0
  const [expandedChartId, setExpandedChartId] = useExpandedChartId()
  const showCategoryGroupingOption = categoricalEntries.length > 0

  const debouncedNumericValueChange = React.useMemo(
    () =>
      debounce((nodeId: number, value: string) => {
        modelEditorApi?.updateNumericConstant(nodeId, { value })
      }, 400),
    [modelEditorApi]
  )

  const debouncedCatMapValueChange = React.useMemo(
    () =>
      debounce((nodeId: number, categoryValue: number, numericValue: string) => {
        modelEditorApi?.updateCategoricalToNumeric(nodeId, { mapValue: { categoryValue, numericValue } })
      }, 400),
    [modelEditorApi]
  )

  const debouncedPayoffCellChange = React.useMemo(
    () =>
      debounce((nodeId: number, aValue: number, bValue: number, numericValue: string) => {
        modelEditorApi?.updatePayoffMatrix(nodeId, { cell: { aValue, bValue, numericValue } })
      }, 400),
    [modelEditorApi]
  )

  const debouncedTableCellChange = React.useMemo(
    () =>
      debounce((nodeId: number, rowIndex: number, columnIndex: number, value: string) => {
        modelEditorApi?.updateTable(nodeId, { cell: { rowIndex, columnIndex, value } })
      }, 400),
    [modelEditorApi]
  )

  const debouncedTableRowNameChange = React.useMemo(
    () =>
      debounce((nodeId: number, index: number, name: string) => {
        modelEditorApi?.updateTable(nodeId, { rowName: { index, name } })
      }, 400),
    [modelEditorApi]
  )

  const debouncedTableColumnNameChange = React.useMemo(
    () =>
      debounce((nodeId: number, index: number, name: string) => {
        modelEditorApi?.updateTable(nodeId, { columnName: { index, name } })
      }, 400),
    [modelEditorApi]
  )

  React.useEffect(() => () => debouncedNumericValueChange.cancel(), [debouncedNumericValueChange])
  React.useEffect(() => () => debouncedCatMapValueChange.cancel(), [debouncedCatMapValueChange])
  React.useEffect(() => () => debouncedPayoffCellChange.cancel(), [debouncedPayoffCellChange])
  React.useEffect(() => () => debouncedTableCellChange.cancel(), [debouncedTableCellChange])
  React.useEffect(() => () => debouncedTableRowNameChange.cancel(), [debouncedTableRowNameChange])
  React.useEffect(
    () => () => debouncedTableColumnNameChange.cancel(),
    [debouncedTableColumnNameChange]
  )

  const flushNumericValueChange = React.useCallback(
    (nodeId: number, value: string) => {
      debouncedNumericValueChange.cancel()
      modelEditorApi?.updateNumericConstant(nodeId, { value })
    },
    [debouncedNumericValueChange, modelEditorApi]
  )

  const flushCatMapValueChange = React.useCallback(
    (nodeId: number, categoryValue: number, numericValue: string) => {
      debouncedCatMapValueChange.cancel()
      modelEditorApi?.updateCategoricalToNumeric(nodeId, { mapValue: { categoryValue, numericValue } })
    },
    [debouncedCatMapValueChange, modelEditorApi]
  )

  const flushPayoffCellChange = React.useCallback(
    (nodeId: number, aValue: number, bValue: number, numericValue: string) => {
      debouncedPayoffCellChange.cancel()
      modelEditorApi?.updatePayoffMatrix(nodeId, { cell: { aValue, bValue, numericValue } })
    },
    [debouncedPayoffCellChange, modelEditorApi]
  )

  const flushTableCellChange = React.useCallback(
    (nodeId: number, rowIndex: number, columnIndex: number, value: string) => {
      debouncedTableCellChange.cancel()
      modelEditorApi?.updateTable(nodeId, { cell: { rowIndex, columnIndex, value } })
    },
    [debouncedTableCellChange, modelEditorApi]
  )

  const flushTableRowNameChange = React.useCallback(
    (nodeId: number, index: number, name: string) => {
      debouncedTableRowNameChange.cancel()
      modelEditorApi?.updateTable(nodeId, { rowName: { index, name } })
    },
    [debouncedTableRowNameChange, modelEditorApi]
  )

  const flushTableColumnNameChange = React.useCallback(
    (nodeId: number, index: number, name: string) => {
      debouncedTableColumnNameChange.cancel()
      modelEditorApi?.updateTable(nodeId, { columnName: { index, name } })
    },
    [debouncedTableColumnNameChange, modelEditorApi]
  )

  const handleGroupByCategoryChange = React.useCallback((checked: boolean) => {
    setGroupByCategory(checked)
    writeDetailGroupByCategory(checked)
  }, [])

  const [categoryMatrixExpanded, setCategoryMatrixExpanded] = React.useState(false)

  const showCategoryMatrixTools =
    groupByCategory && categoryMatrix.columns.length > 0 && categoryMatrix.rows.length > 0

  const handleCategoryMatrixCsvImport = React.useCallback(
    (csvText: string) => {
      const updates = parseCategoricalDetailMatrixCsv(csvText, categoryMatrix)
      if (updates.length > 0) {
        modelEditorApi?.importCategoricalDetailMatrix(updates)
      }
    },
    [categoryMatrix, modelEditorApi]
  )

  const highlightScrollRef = React.useRef<HTMLElement | null>(null)
  const expandedHighlightScrollRef = React.useRef<HTMLElement | null>(null)

  React.useEffect(() => {
    if (!categoryMatrixExpanded) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCategoryMatrixExpanded(false)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [categoryMatrixExpanded])

  React.useEffect(() => {
    const scrollTarget = categoryMatrixExpanded
      ? expandedHighlightScrollRef.current
      : highlightScrollRef.current
    scrollTarget?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [
    highlightedCategoryGroupKey,
    groupByCategory,
    categoryMatrix,
    categoricalEntries,
    categoryMatrixExpanded,
  ])

  React.useEffect(() => {
    if (expandedChartId == null) {
      return
    }
    if (getChartDetailEntry(model, expandedChartId) == null) {
      setExpandedChartId(null)
    }
  }, [model, expandedChartId, setExpandedChartId])

  if (!hasEntries) {
    return (
      <div
        className="model-detail-panel flex-grow-1 bg-white text-muted model-detail-panel__empty"
        style={{ overflowY: "auto" }}
      >
        <p className="mb-0">
          No model parameters are pinned here yet. On the model view, open a Numeric constant, Categorical to
          numeric dataset, Payoff matrix, New Table, or chart node and use the eye icon to expose values in this
          panel.
        </p>
      </div>
    )
  }

  return (
    <div
      className="model-detail-panel flex-grow-1 bg-white d-flex flex-column"
      style={{ overflowY: "auto", flexBasis: "0px", minHeight: 0 }}
    >
      {showCategoryGroupingOption && (
        <div className="model-detail-panel__toolbar border-bottom bg-light d-flex align-items-center flex-wrap">
          <label className="mb-0 d-flex align-items-center" style={{ cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox"
              className="mr-1"
              style={{ marginTop: 0 }}
              checked={groupByCategory}
              onChange={e => handleGroupByCategoryChange(e.target.checked)}
            />
            Group by category
          </label>
          {showCategoryMatrixTools && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-secondary model-detail-panel__matrix-expand-btn ml-auto"
              onClick={() => setCategoryMatrixExpanded(true)}
            >
              <i className="fas fa-expand-alt mr-1" aria-hidden="true" />
              Expand table
            </button>
          )}
        </div>
      )}
      {chartEntries.length > 0 && (
        <div className="model-detail-panel__chart-tabs w-100" role="tablist" aria-label="Exposed charts">
          {chartEntries.map(entry => {
            const selected = expandedChartId === entry.nodeId
            return (
              <button
                key={`chart-tab-${entry.nodeId}`}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`model-detail-panel__chart-tab w-100${selected ? " model-detail-panel__chart-tab--active" : ""}`}
                title={entry.name}
                onClick={() => setExpandedChartId(selected ? null : entry.nodeId)}
              >
                <span
                  className={`model-detail-panel__chart-tab-icon model-detail-panel__chart-tab-icon--${entry.kind}`}
                  aria-hidden="true"
                >
                  <i className={`fas ${chartDetailIcon(entry.kind)}`} />
                </span>
                <span className="model-detail-panel__chart-tab-text">
                  <span className="model-detail-panel__chart-tab-name">{entry.name}</span>
                  <span className="model-detail-panel__chart-tab-kind">
                    {chartDetailKindLabel(entry.kind)}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      )}
      <div className="flex-grow-1" style={{ overflowY: "auto" }}>
        {!hasParameterEntries && chartEntries.length > 0 && (
          <p className="text-muted mb-0 model-detail-panel__empty">
            Click a chart tab to expand and view it.
          </p>
        )}
        {numericEntries.length > 0 && (
          <div className="model-detail-panel__numeric-grid">
            {numericEntries.map(entry => (
              <NumericConstantDetailRow
                key={`nc-${entry.nodeId}`}
                entry={entry}
                onValueChange={value => debouncedNumericValueChange(entry.nodeId, value)}
                onValueBlur={value => flushNumericValueChange(entry.nodeId, value)}
              />
            ))}
          </div>
        )}
        {groupByCategory && categoryMatrix.columns.length > 0 ? (
          <>
            <CategoricalDetailMatrixTable
              matrix={categoryMatrix}
              size="compact"
              highlightedCategoryGroupKey={highlightedCategoryGroupKey}
              highlightScrollRef={categoryMatrixExpanded ? undefined : highlightScrollRef}
              onMapValueChange={(nodeId, categoryValue, numericValue) =>
                debouncedCatMapValueChange(nodeId, categoryValue, numericValue)
              }
              onMapValueBlur={(nodeId, categoryValue, numericValue) =>
                flushCatMapValueChange(nodeId, categoryValue, numericValue)
              }
            />
            <CategoricalDetailMatrixExpandedDialog
              open={categoryMatrixExpanded}
              matrix={categoryMatrix}
              highlightedCategoryGroupKey={highlightedCategoryGroupKey}
              highlightScrollRef={expandedHighlightScrollRef}
              onClose={() => setCategoryMatrixExpanded(false)}
              onImport={handleCategoryMatrixCsvImport}
              onMapValueChange={(nodeId, categoryValue, numericValue) =>
                debouncedCatMapValueChange(nodeId, categoryValue, numericValue)
              }
              onMapValueBlur={(nodeId, categoryValue, numericValue) =>
                flushCatMapValueChange(nodeId, categoryValue, numericValue)
              }
            />
          </>
        ) : (
          !groupByCategory &&
          categoricalEntries.map(entry => (
              <CategoricalToNumericDetailSection
                key={`cat-${entry.nodeId}`}
                entry={entry}
                highlightedCategoryGroupKey={highlightedCategoryGroupKey}
                highlightScrollRef={highlightScrollRef}
                onMapValueChange={(categoryValue, numericValue) =>
                  debouncedCatMapValueChange(entry.nodeId, categoryValue, numericValue)
                }
                onMapValueBlur={(categoryValue, numericValue) =>
                  flushCatMapValueChange(entry.nodeId, categoryValue, numericValue)
                }
              />
            ))
        )}
        {payoffEntries.map(entry => (
          <PayoffMatrixDetailSection
            key={`payoff-${entry.nodeId}`}
            entry={entry}
            model={model}
            modelEditorApi={modelEditorApi}
            onCellChange={(aValue, bValue, numericValue) =>
              debouncedPayoffCellChange(entry.nodeId, aValue, bValue, numericValue)
            }
            onCellBlur={(aValue, bValue, numericValue) =>
              flushPayoffCellChange(entry.nodeId, aValue, bValue, numericValue)
            }
          />
        ))}
        {tableEntries.map(entry => (
          <TableDetailSection
            key={`table-${entry.nodeId}`}
            entry={entry}
            model={model}
            modelEditorApi={modelEditorApi}
            onCellChange={(rowIndex, columnIndex, value) =>
              debouncedTableCellChange(entry.nodeId, rowIndex, columnIndex, value)
            }
            onCellBlur={(rowIndex, columnIndex, value) =>
              flushTableCellChange(entry.nodeId, rowIndex, columnIndex, value)
            }
            onRowNameChange={(index, name) =>
              debouncedTableRowNameChange(entry.nodeId, index, name)
            }
            onRowNameBlur={(index, name) => flushTableRowNameChange(entry.nodeId, index, name)}
            onColumnNameChange={(index, name) =>
              debouncedTableColumnNameChange(entry.nodeId, index, name)
            }
            onColumnNameBlur={(index, name) =>
              flushTableColumnNameChange(entry.nodeId, index, name)
            }
            highlightedCategoryGroupKey={highlightedCategoryGroupKey}
          />
        ))}
      </div>
    </div>
  )
}

type ChartDetailExpandedDialogProps = {
  entry: ChartDetailEntry | null
  onClose: () => void
  contained?: boolean
}

export const ChartDetailExpandedDialog = ({ entry, onClose, contained = false }: ChartDetailExpandedDialogProps) => {
  React.useEffect(() => {
    if (entry == null) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [entry, onClose])

  if (entry == null) {
    return null
  }

  const dialogTitleId = `chartExpandedTitle-${entry.nodeId}`

  return createPortal(
    <div
      className={`modal d-block model-detail-panel__matrix-modal model-detail-panel__matrix-modal--chart${contained ? " model-detail-panel__matrix-modal--contained" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogTitleId}
      style={{ backgroundColor: "rgba(0, 0, 0, 0.45)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered model-detail-panel__matrix-modal-dialog"
        role="document"
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header py-2 d-flex align-items-center">
            <h5 className="modal-title mb-0" id={dialogTitleId}>
              {entry.name}
            </h5>
            <button type="button" className="close ml-auto" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div
            className={`modal-body model-detail-panel__matrix-modal-body model-detail-panel__chart-modal-body${
              entry.kind === "sankey" ? " model-detail-panel__chart-modal-body--sankey" : ""
            }`}
          >
            {!chartEntryHasData(entry) ? (
              <p className="text-muted mb-0">
                This chart has no data yet. Connect inputs on the model view to populate it.
              </p>
            ) : (
              <ChartDetailBody entry={entry} expanded contained={contained} />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

type PayoffMatrixDetailSectionProps = {
  entry: PayoffMatrixDetailEntry
  model: Data | null
  modelEditorApi: ModelEditorApi | null
  onCellChange: (aValue: number, bValue: number, numericValue: string) => void
  onCellBlur: (aValue: number, bValue: number, numericValue: string) => void
}

const payoffNodeData = (model: Data | null, nodeId: number): Record<string, unknown> => {
  const nodes = model?.nodes as Record<string, { data?: Record<string, unknown> }> | undefined
  if (!nodes) {
    return {}
  }
  const node = nodes[String(nodeId)] ?? nodes[nodeId]
  return node?.data ?? {}
}

const payoffMatrixExportFilename = (entry: PayoffMatrixDetailEntry): string => {
  const name = entry.name.trim()
  return name ? `${name}-payoff-matrix` : "payoff-matrix"
}

type PayoffMatrixDetailTableSize = "compact" | "expanded"

type PayoffMatrixDetailTableProps = {
  axes: PayoffAxesConfig
  data: Record<string, unknown>
  size?: PayoffMatrixDetailTableSize
  onCellChange: (aValue: number, bValue: number, numericValue: string) => void
  onCellBlur: (aValue: number, bValue: number, numericValue: string) => void
}

const PayoffMatrixDetailTable = ({
  axes,
  data,
  size = "compact",
  onCellChange,
  onCellBlur,
}: PayoffMatrixDetailTableProps) => {
  const isExpandedLayout = size === "expanded"
  const verticalHeaders = isExpandedLayout || axes.a.length * axes.b.length > 4

  return (
    <div
      className={`model-detail-panel__matrix-wrap${isExpandedLayout ? " model-detail-panel__matrix-wrap--expanded" : ""}`}
    >
      <table
        className={`model-detail-panel__matrix model-detail-panel__matrix--payoff${isExpandedLayout ? " model-detail-panel__matrix--expanded" : ""}`}
      >
        <colgroup>
          <col className="model-detail-panel__matrix-col-label" />
          {axes.b.map(b => (
            <col
              key={b.value}
              className={
                verticalHeaders
                  ? "model-detail-panel__matrix-col-value model-detail-panel__matrix-col-value--vertical"
                  : "model-detail-panel__matrix-col-value"
              }
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="model-detail-panel__matrix-corner" aria-hidden="true" />
            {axes.b.map(b => (
              <th
                key={b.value}
                className={`model-detail-panel__matrix-col-head${verticalHeaders ? " model-detail-panel__matrix-col-head--vertical" : ""}`}
                title={b.label}
              >
                <span className="model-detail-panel__matrix-col-head-text">{b.label}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {axes.a.map(a => (
            <tr key={a.value}>
              <th className="model-detail-panel__matrix-row-head" title={a.label} scope="row">
                {a.label}
              </th>
              {axes.b.map(b => (
                <td key={b.value} className="model-detail-panel__matrix-cell">
                  <div className="model-detail-panel__matrix-cell-inner">
                    <PayoffMatrixDetailCell
                      aLabel={a.label}
                      bLabel={b.label}
                      value={payoffMatrixCellValueString(data, a.value, b.value, axes)}
                      expanded={isExpandedLayout}
                      onValueChange={numericValue => onCellChange(a.value, b.value, numericValue)}
                      onValueBlur={numericValue => onCellBlur(a.value, b.value, numericValue)}
                    />
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

type PayoffMatrixCsvActionsProps = {
  axes: PayoffAxesConfig
  getValue: (key: string) => string
  filename: string
  onImport: (values: Record<string, string>) => void
  className?: string
}

const PayoffMatrixCsvActions = ({
  axes,
  getValue,
  filename,
  onImport,
  className,
}: PayoffMatrixCsvActionsProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleImportCsv = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0]
    if (!file) {
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const updates = parsePayoffMatrixCsv(String(reader.result || ""), axes)
      if (Object.keys(updates).length > 0) {
        onImport(updates)
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
    reader.readAsText(file)
  }

  return (
    <div className={`model-detail-panel__matrix-csv-actions d-flex align-items-center${className ? ` ${className}` : ""}`}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary model-detail-panel__matrix-csv-btn"
        onClick={() => fileInputRef.current?.click()}
      >
        <i className="fa fa-upload mr-1" aria-hidden="true" />
        Import CSV
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary model-detail-panel__matrix-csv-btn ml-1"
        onClick={() => downloadPayoffMatrixCsv(axes, getValue, filename)}
      >
        <i className="fa fa-download mr-1" aria-hidden="true" />
        Export CSV
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="model-detail-panel__matrix-csv-file"
        onChange={handleImportCsv}
      />
    </div>
  )
}

type PayoffMatrixDetailExpandedDialogProps = {
  open: boolean
  entry: PayoffMatrixDetailEntry
  model: Data | null
  onClose: () => void
  onImport: (values: Record<string, string>) => void
  onCellChange: (aValue: number, bValue: number, numericValue: string) => void
  onCellBlur: (aValue: number, bValue: number, numericValue: string) => void
}

const PayoffMatrixDetailExpandedDialog = ({
  open,
  entry,
  model,
  onClose,
  onImport,
  onCellChange,
  onCellBlur,
}: PayoffMatrixDetailExpandedDialogProps) => {
  const { axes } = entry
  const data = payoffNodeData(model, entry.nodeId)
  const dialogTitleId = `payoffMatrixExpandedTitle-${entry.nodeId}`

  const getValue = React.useCallback(
    (key: string) => {
      const raw = data[key]
      if (typeof raw === "number" && Number.isFinite(raw)) {
        return String(raw)
      }
      if (typeof raw === "string") {
        return raw
      }
      return ""
    },
    [data]
  )

  React.useEffect(() => {
    if (!open) {
      return
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, onClose])

  if (!open) {
    return null
  }

  return createPortal(
    <div
      className="modal d-block model-detail-panel__matrix-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogTitleId}
      style={{ backgroundColor: "rgba(0, 0, 0, 0.45)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered model-detail-panel__matrix-modal-dialog"
        role="document"
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header py-2 d-flex align-items-center">
            <h5 className="modal-title mb-0" id={dialogTitleId}>
              {entry.name}
            </h5>
            <PayoffMatrixCsvActions
              axes={axes}
              getValue={getValue}
              filename={payoffMatrixExportFilename(entry)}
              onImport={onImport}
              className="ml-3"
            />
            <button type="button" className="close ml-auto" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body model-detail-panel__matrix-modal-body">
            <PayoffMatrixDetailTable
              axes={axes}
              data={data}
              size="expanded"
              onCellChange={onCellChange}
              onCellBlur={onCellBlur}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

const PayoffMatrixDetailSection = ({
  entry,
  model,
  modelEditorApi,
  onCellChange,
  onCellBlur,
}: PayoffMatrixDetailSectionProps) => {
  const [tableExpanded, setTableExpanded] = React.useState(false)
  const data = payoffNodeData(model, entry.nodeId)

  const handleImport = React.useCallback(
    (values: Record<string, string>) => {
      modelEditorApi?.importPayoffMatrixValues(entry.nodeId, values)
    },
    [entry.nodeId, modelEditorApi]
  )

  return (
    <>
      <div className="border-bottom">
        <div className="model-detail-panel__header text-dark bg-light border-bottom d-flex align-items-center">
          <span className="text-truncate flex-grow-1" title={entry.name}>
            {entry.name}
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-secondary model-detail-panel__matrix-expand-btn flex-shrink-0"
            onClick={() => setTableExpanded(true)}
          >
            <i className="fas fa-expand-alt mr-1" aria-hidden="true" />
            Expand table
          </button>
        </div>
        <PayoffMatrixDetailTable
          axes={entry.axes}
          data={data}
          size="compact"
          onCellChange={onCellChange}
          onCellBlur={onCellBlur}
        />
      </div>
      <PayoffMatrixDetailExpandedDialog
        open={tableExpanded}
        entry={entry}
        model={model}
        onClose={() => setTableExpanded(false)}
        onImport={handleImport}
        onCellChange={onCellChange}
        onCellBlur={onCellBlur}
      />
    </>
  )
}

type PayoffMatrixDetailCellProps = {
  aLabel: string
  bLabel: string
  value: string
  expanded?: boolean
  onValueChange: (numericValue: string) => void
  onValueBlur: (numericValue: string) => void
}

const PayoffMatrixDetailCell = ({
  aLabel,
  bLabel,
  value: initialValue,
  expanded,
  onValueChange,
  onValueBlur,
}: PayoffMatrixDetailCellProps) => {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue, aLabel, bLabel])

  return (
    <DetailNumericInput
      value={value}
      compact={!expanded}
      expanded={expanded}
      ariaLabel={`Payoff for A: ${aLabel}, B: ${bLabel}`}
      onChange={next => {
        setValue(next)
        onValueChange(next)
      }}
      onBlur={next => onValueBlur(next)}
    />
  )
}

type TableDetailSectionProps = {
  entry: TableDetailEntry
  model: Data | null
  modelEditorApi: ModelEditorApi | null
  onCellChange: (rowIndex: number, columnIndex: number, value: string) => void
  onCellBlur: (rowIndex: number, columnIndex: number, value: string) => void
  onRowNameChange: (index: number, name: string) => void
  onRowNameBlur: (index: number, name: string) => void
  onColumnNameChange: (index: number, name: string) => void
  onColumnNameBlur: (index: number, name: string) => void
  highlightedCategoryGroupKey?: string | null
}

const tableExportFilename = (entry: TableDetailEntry): string => {
  const name = entry.name.trim()
  return name || "table"
}

type TableDetailTableSize = "compact" | "expanded"

const ROW_HEAT_STOPS: Array<[number, number, number]> = [
  [255, 252, 240],
  [255, 232, 186],
  [250, 198, 140],
  [242, 158, 108],
  [214, 118, 96],
]

function lerpChannel(start: number, end: number, t: number): number {
  return Math.round(start + (end - start) * t)
}

function interpolateRgbStops(
  stops: Array<[number, number, number]>,
  t: number
): [number, number, number] {
  const clamped = Math.min(1, Math.max(0, t))
  const scaled = clamped * (stops.length - 1)
  const index = Math.min(stops.length - 2, Math.floor(scaled))
  const fraction = scaled - index
  const from = stops[index]
  const to = stops[index + 1]
  return [
    lerpChannel(from[0], to[0], fraction),
    lerpChannel(from[1], to[1], fraction),
    lerpChannel(from[2], to[2], fraction),
  ]
}

const CHART_POSITIVE_STOPS: Array<[number, number, number]> = [
  [198, 232, 197],
  [129, 199, 132],
  [67, 160, 71],
  [27, 94, 32],
]

const CHART_NEGATIVE_STOPS: Array<[number, number, number]> = [
  [255, 205, 210],
  [239, 154, 154],
  [229, 57, 53],
  [183, 28, 28],
]

function chartBarColor(
  value: number,
  scale: { minVal: number; maxVal: number }
): { color: string; darkText: boolean } {
  const t =
    value < 0
      ? scale.minVal === 0
        ? 1
        : Math.min(1, Math.abs(value) / Math.abs(scale.minVal))
      : scale.maxVal === 0
        ? 1
        : Math.min(1, value / scale.maxVal)
  const rgb = interpolateRgbStops(
    value < 0 ? CHART_NEGATIVE_STOPS : CHART_POSITIVE_STOPS,
    0.15 + 0.85 * t
  )
  return {
    color: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`,
    darkText: heatNeedsLightText(rgb),
  }
}

function chartAxisTicks(minVal: number, maxVal: number): number[] {
  const ticks = new Set<number>([minVal, maxVal])
  if (minVal < 0 && maxVal > 0) {
    ticks.add(0)
    const negativeMid = Math.round(minVal / 2)
    const positiveMid = Math.round(maxVal / 2)
    if (negativeMid !== minVal && negativeMid !== 0) ticks.add(negativeMid)
    if (positiveMid !== maxVal && positiveMid !== 0) ticks.add(positiveMid)
  } else {
    const mid = Math.round((minVal + maxVal) / 2)
    if (mid !== minVal && mid !== maxVal) ticks.add(mid)
  }
  return Array.from(ticks).sort((a, b) => a - b)
}

function heatNeedsLightText(rgb: [number, number, number]): boolean {
  const linear = (channel: number) => {
    const srgb = channel / 255
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4
  }
  const luminance = 0.2126 * linear(rgb[0]) + 0.7152 * linear(rgb[1]) + 0.0722 * linear(rgb[2])
  return luminance < 0.38
}

function rowHeatStyles(
  cells: TableDetailEntry["cells"],
  rowIndex: number,
  columnCount: number,
  visibleColumnIndices?: Set<number>
): Array<{ color: string; darkText: boolean } | null> {
  const values = Array.from({ length: columnCount }, (_, columnIndex) => {
    if (visibleColumnIndices != null && !visibleColumnIndices.has(columnIndex)) return null
    const value = cells[rowIndex]?.[columnIndex]
    return typeof value === "number" && Number.isFinite(value) ? value : null
  })
  const finite = values.filter((value): value is number => value !== null)
  if (finite.length === 0) return values.map(() => null)

  const min = Math.min(...finite)
  const max = Math.max(...finite)
  const span = max - min

  return values.map(value => {
    if (value === null) return null
    const t = span === 0 ? 0.55 : (value - min) / span
    const rgb = interpolateRgbStops(ROW_HEAT_STOPS, t)
    return {
      color: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`,
      darkText: heatNeedsLightText(rgb),
    }
  })
}

function tableRowNumericValues(
  cells: TableDetailEntry["cells"],
  rowIndex: number,
  columnCount: number
): Array<number | null> {
  return Array.from({ length: columnCount }, (_, columnIndex) => {
    const value = cells[rowIndex]?.[columnIndex]
    return typeof value === "number" && Number.isFinite(value) ? value : null
  })
}

function tableColumnNumericValues(
  cells: TableDetailEntry["cells"],
  columnIndex: number,
  rowCount: number
): Array<number | null> {
  return Array.from({ length: rowCount }, (_, rowIndex) => {
    const value = cells[rowIndex]?.[columnIndex]
    return typeof value === "number" && Number.isFinite(value) ? value : null
  })
}

function formatTableChartValue(value: number): string {
  if (Number.isInteger(value)) return String(value)
  return parseFloat(value.toPrecision(4)).toString()
}

function sharedChartScale(valueLists: Array<Array<number | null>>) {
  const finite: number[] = []
  for (const values of valueLists) {
    for (const value of values) {
      if (value !== null) finite.push(value)
    }
  }
  if (finite.length === 0) {
    return { minVal: 0, maxVal: 1, span: 1, zeroPct: 0 }
  }

  const min = Math.min(...finite)
  const max = Math.max(...finite)
  let minVal: number
  let maxVal: number
  if (min >= 0) {
    minVal = 0
    maxVal = Math.ceil(max)
  } else {
    const extent = Math.max(Math.abs(min), max)
    minVal = Math.floor(-extent)
    maxVal = Math.ceil(extent)
  }
  const span = maxVal - minVal || 1
  return {
    minVal,
    maxVal,
    span,
    zeroPct: ((0 - minVal) / span) * 100,
  }
}

function rowChartBarLayout(value: number, scale: { minVal: number; span: number }) {
  const widthPct = (Math.abs(value) / scale.span) * 100
  const leftPct =
    value >= 0
      ? ((0 - scale.minVal) / scale.span) * 100
      : ((value - scale.minVal) / scale.span) * 100
  return { widthPct, leftPct, negative: value < 0 }
}

type TableDetailTableProps = {
  entry: TableDetailEntry
  size?: TableDetailTableSize
  onCellChange: (rowIndex: number, columnIndex: number, value: string) => void
  onCellBlur: (rowIndex: number, columnIndex: number, value: string) => void
  onRowNameChange: (index: number, name: string) => void
  onRowNameBlur: (index: number, name: string) => void
  onColumnNameChange: (index: number, name: string) => void
  onColumnNameBlur: (index: number, name: string) => void
  highlightedCategoryGroupKey?: string | null
  columnHighlightScrollRef?: React.RefObject<HTMLElement | null>
  visibleRowIndices?: Set<number>
  visibleColumnIndices?: Set<number>
}

const TableDetailTable = ({
  entry,
  size = "compact",
  onCellChange,
  onCellBlur,
  onRowNameChange,
  onRowNameBlur,
  onColumnNameChange,
  onColumnNameBlur,
  highlightedCategoryGroupKey = null,
  columnHighlightScrollRef,
  visibleRowIndices,
  visibleColumnIndices,
}: TableDetailTableProps) => {
  const { rows, columns, cells, columnsLocked } = entry
  const isExpandedLayout = size === "expanded"
  const verticalHeaders = !isExpandedLayout && columns.length > 4
  const shownRowIndices = rows
    .map((_, index) => index)
    .filter(index => visibleRowIndices == null || visibleRowIndices.has(index))
  const shownColumnIndices = columns
    .map((_, index) => index)
    .filter(index => visibleColumnIndices == null || visibleColumnIndices.has(index))
  const rowHeat = React.useMemo(
    () => isExpandedLayout
      ? rows.map((_row, rowIndex) => rowHeatStyles(cells, rowIndex, columns.length, visibleColumnIndices))
      : null,
    [isExpandedLayout, rows, columns.length, cells, visibleColumnIndices]
  )

  const isColumnHighlighted = (columnIndex: number) =>
    highlightedCategoryGroupKey !== null &&
    categoryGroupKeyForLabel(columns[columnIndex]) === highlightedCategoryGroupKey

  if (rows.length === 0 || columns.length === 0) {
    return (
      <p className="model-detail-panel__empty mb-0 px-2 py-2">
        {columnsLocked
          ? "Add a row — columns come from the connected reference dataset."
          : "Empty table — add rows and columns on the model node."}
      </p>
    )
  }

  if (shownRowIndices.length === 0 || shownColumnIndices.length === 0) {
    const missing =
      shownRowIndices.length === 0 && shownColumnIndices.length === 0
        ? "fields or classes"
        : shownRowIndices.length === 0
          ? "fields"
          : "classes"
    return (
      <p className="model-detail-panel__empty mb-0 px-2 py-2">
        No {missing} selected — choose {missing} from the dropdown to show the table.
      </p>
    )
  }

  return (
    <div
      className={`model-detail-panel__matrix-wrap${isExpandedLayout ? " model-detail-panel__matrix-wrap--expanded" : ""}`}
    >
      <table
        className={`model-detail-panel__matrix model-detail-panel__matrix--table${isExpandedLayout ? " model-detail-panel__matrix--expanded" : ""}`}
      >
        <colgroup>
          <col className="model-detail-panel__matrix-col-label" />
          {shownColumnIndices.map(c => (
            <col
              key={`table-col-${c}`}
              className={
                verticalHeaders
                  ? "model-detail-panel__matrix-col-value model-detail-panel__matrix-col-value--vertical"
                  : "model-detail-panel__matrix-col-value"
              }
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="model-detail-panel__matrix-corner" aria-hidden="true" />
            {shownColumnIndices.map(c => {
              const col = columns[c]
              const highlighted = isColumnHighlighted(c)
              return (
              <th
                key={`table-col-head-${c}`}
                ref={highlighted ? columnHighlightScrollRef as React.Ref<HTMLTableHeaderCellElement> : undefined}
                className={`model-detail-panel__matrix-col-head${
                  verticalHeaders ? " model-detail-panel__matrix-col-head--vertical" : ""
                }${highlighted ? " model-detail-panel__matrix-col--highlighted" : ""}`}
                title={col}
              >
                {columnsLocked ? (
                  <span className="model-detail-panel__matrix-col-head-text">{col}</span>
                ) : (
                  <TableDetailHeaderInput
                    value={col}
                    compact={!isExpandedLayout}
                    expanded={isExpandedLayout}
                    ariaLabel={`Column ${c + 1} name`}
                    onChange={name => onColumnNameChange(c, name)}
                    onBlur={name => onColumnNameBlur(c, name)}
                  />
                )}
              </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {shownRowIndices.map(r => {
            const row = rows[r]
            return (
            <tr key={`table-row-${r}`}>
              <th className="model-detail-panel__matrix-row-head" title={row} scope="row">
                <TableDetailHeaderInput
                  value={row}
                  compact={!isExpandedLayout}
                  expanded={isExpandedLayout}
                  ariaLabel={`Row ${r + 1} name`}
                  onChange={name => onRowNameChange(r, name)}
                  onBlur={name => onRowNameBlur(r, name)}
                />
              </th>
              {shownColumnIndices.map(c => {
                const heat = isExpandedLayout ? rowHeat?.[r]?.[c] : undefined
                return (
                <td
                  key={`table-cell-${r}-${c}`}
                  className={`model-detail-panel__matrix-cell${
                    isColumnHighlighted(c) ? " model-detail-panel__matrix-col--highlighted" : ""
                  }${heat ? " model-detail-panel__matrix-cell--heatmap" : ""}${
                    heat?.darkText ? " model-detail-panel__matrix-cell--heat-dark" : ""
                  }`}
                  style={heat ? { ["--heat-color" as string]: heat.color } as React.CSSProperties : undefined}
                >
                  <div className="model-detail-panel__matrix-cell-inner">
                    <TableDetailCell
                      rowLabel={row}
                      columnLabel={columns[c]}
                      value={tableCellValueString(cells, r, c)}
                      expanded={isExpandedLayout}
                      onValueChange={value => onCellChange(r, c, value)}
                      onValueBlur={value => onCellBlur(r, c, value)}
                    />
                  </div>
                </td>
                )
              })}
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

type ChartGroupBy = "field" | "class"

function toggleIndexInSet(selected: Set<number>, index: number): Set<number> {
  const next = new Set(selected)
  if (next.has(index)) next.delete(index)
  else next.add(index)
  return next
}

function visibilityAxisLabel(selectedCount: number, total: number, plural: string): string {
  if (total === 0 || selectedCount === 0) return `No ${plural}`
  if (selectedCount === total) return `All ${plural}`
  return `${selectedCount} of ${total} ${plural}`
}

const TableDetailVisibilityFilterSection = ({
  title,
  items,
  selectedIndices,
  onSelectedIndicesChange,
  fallbackSingular,
  query,
}: {
  title: string
  items: string[]
  selectedIndices: Set<number>
  onSelectedIndicesChange: (next: Set<number>) => void
  fallbackSingular: string
  query: string
}) => {
  const itemLabel = (itemName: string, index: number) =>
    itemName || `${fallbackSingular} ${index + 1}`
  const searchActive = query.trim().length > 0
  const visibleItems = items
    .map((name, index) => ({ name, index, label: itemLabel(name, index) }))
    .filter(({ label }) => {
      const needle = query.trim().toLowerCase()
      return needle.length === 0 || label.toLowerCase().includes(needle)
    })
  const allVisibleSelected =
    visibleItems.length > 0 && visibleItems.every(({ index }) => selectedIndices.has(index))
  const someVisibleSelected =
    visibleItems.some(({ index }) => selectedIndices.has(index)) && !allVisibleSelected
  const onlyResultsSelected =
    searchActive &&
    visibleItems.length > 0 &&
    selectedIndices.size === visibleItems.length &&
    visibleItems.every(({ index }) => selectedIndices.has(index))
  const previousSelectionRef = React.useRef<Set<number> | null>(null)

  React.useEffect(() => {
    previousSelectionRef.current = null
  }, [query])

  const toggleVisible = (checked: boolean) => {
    const next = new Set(selectedIndices)
    for (const { index } of visibleItems) {
      if (checked) next.add(index)
      else next.delete(index)
    }
    onSelectedIndicesChange(next)
  }

  const toggleOnlyResults = (checked: boolean) => {
    if (checked) {
      previousSelectionRef.current = new Set(selectedIndices)
      onSelectedIndicesChange(new Set(visibleItems.map(({ index }) => index)))
      return
    }
    const restored = previousSelectionRef.current
    previousSelectionRef.current = null
    onSelectedIndicesChange(
      restored ?? new Set(items.map((_, index) => index))
    )
  }

  return (
    <div className="model-detail-panel__row-filter-section">
      <div className="model-detail-panel__row-filter-section-head">
        <div className="model-detail-panel__row-filter-section-title">{title}</div>
        <div className="model-detail-panel__row-filter-section-actions">
          <label className="model-detail-panel__row-filter-item model-detail-panel__row-filter-item--all">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              disabled={visibleItems.length === 0}
              ref={element => {
                if (element) element.indeterminate = someVisibleSelected
              }}
              onChange={event => toggleVisible(event.target.checked)}
            />
            Select all
          </label>
          {searchActive && (
            <label className="model-detail-panel__row-filter-item model-detail-panel__row-filter-item--all">
              <input
                type="checkbox"
                checked={onlyResultsSelected}
                disabled={visibleItems.length === 0}
                onChange={event => toggleOnlyResults(event.target.checked)}
              />
              Only results
            </label>
          )}
        </div>
      </div>
      {visibleItems.length === 0 ? (
        <p className="model-detail-panel__row-filter-empty mb-0">
          No matching {title.toLowerCase()}
        </p>
      ) : (
        <div className="model-detail-panel__row-filter-grid">
          {visibleItems.map(({ label, index }) => (
            <label key={`${title}-${index}`} className="dropdown-item model-detail-panel__row-filter-item">
              <input
                type="checkbox"
                checked={selectedIndices.has(index)}
                onChange={() => onSelectedIndicesChange(toggleIndexInSet(selectedIndices, index))}
              />
              <span title={label}>{label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

const TableDetailVisibilityFilter = ({
  fields,
  classes,
  selectedFieldIndices,
  selectedClassIndices,
  onSelectedFieldIndicesChange,
  onSelectedClassIndicesChange,
}: {
  fields: string[]
  classes: string[]
  selectedFieldIndices: Set<number>
  selectedClassIndices: Set<number>
  onSelectedFieldIndicesChange: (next: Set<number>) => void
  onSelectedClassIndicesChange: (next: Set<number>) => void
}) => {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const rootRef = React.useRef<HTMLDivElement>(null)
  const fieldsAll = fields.length > 0 && fields.every((_, index) => selectedFieldIndices.has(index))
  const classesAll = classes.length > 0 && classes.every((_, index) => selectedClassIndices.has(index))
  const label =
    fieldsAll && classesAll
      ? "All fields/classes"
      : selectedFieldIndices.size === 0 && selectedClassIndices.size === 0
        ? "No fields/classes"
        : `${visibilityAxisLabel(selectedFieldIndices.size, fields.length, "fields")}, ${visibilityAxisLabel(selectedClassIndices.size, classes.length, "classes")}`

  React.useEffect(() => {
    if (!open) {
      setQuery("")
      return
    }
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return
      event.stopPropagation()
      event.preventDefault()
      if (query.trim()) {
        setQuery("")
        return
      }
      setOpen(false)
    }
    document.addEventListener("mousedown", onPointerDown)
    window.addEventListener("keydown", onKeyDown, true)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      window.removeEventListener("keydown", onKeyDown, true)
    }
  }, [open, query])

  return (
    <div className="dropdown model-detail-panel__row-filter" ref={rootRef}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary dropdown-toggle"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        {label}
      </button>
      {open && (
        <div className="dropdown-menu show model-detail-panel__row-filter-menu" role="menu">
          <div className="model-detail-panel__row-filter-search">
            <input
              type="search"
              className="form-control form-control-sm"
              value={query}
              autoFocus
              placeholder="Search fields and classes"
              aria-label="Search fields and classes"
              onChange={event => setQuery(event.target.value)}
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
            />
          </div>
          <TableDetailVisibilityFilterSection
            title="Fields"
            items={fields}
            selectedIndices={selectedFieldIndices}
            onSelectedIndicesChange={onSelectedFieldIndicesChange}
            fallbackSingular="Field"
            query={query}
          />
          <div className="model-detail-panel__row-filter-separator" role="separator" />
          <TableDetailVisibilityFilterSection
            title="Classes"
            items={classes}
            selectedIndices={selectedClassIndices}
            onSelectedIndicesChange={onSelectedClassIndicesChange}
            fallbackSingular="Class"
            query={query}
          />
        </div>
      )}
    </div>
  )
}

const TableDetailChartView = ({
  entry,
  highlightedCategoryGroupKey = null,
  groupBy = "field",
  visibleRowIndices,
  visibleColumnIndices,
}: {
  entry: TableDetailEntry
  highlightedCategoryGroupKey?: string | null
  groupBy?: ChartGroupBy
  visibleRowIndices?: Set<number>
  visibleColumnIndices?: Set<number>
}) => {
  const { rows, columns, cells } = entry
  const isClass = groupBy === "class"
  const shownRows = rows
    .map((name, index) => ({ name, index }))
    .filter(({ index }) => visibleRowIndices == null || visibleRowIndices.has(index))
  const shownColumns = columns
    .map((name, index) => ({ name, index }))
    .filter(({ index }) => visibleColumnIndices == null || visibleColumnIndices.has(index))
  const charts = isClass
    ? shownColumns.map(({ name, index }) => {
        const columnValues = tableColumnNumericValues(cells, index, rows.length)
        return {
          key: `col-${index}`,
          title: name || `Class ${index + 1}`,
          values: shownRows.map(({ index: rowIndex }) => columnValues[rowIndex]),
          barLabels: shownRows.map(
            ({ name: rowName, index: rowIndex }) => rowName || `Field ${rowIndex + 1}`
          ),
          chartHighlighted:
            highlightedCategoryGroupKey !== null &&
            categoryGroupKeyForLabel(name) === highlightedCategoryGroupKey,
          barHighlighted: (_barIndex: number) => false,
        }
      })
    : shownRows.map(({ name, index }) => {
        const rowValues = tableRowNumericValues(cells, index, columns.length)
        return {
          key: `row-${index}`,
          title: name || `Field ${index + 1}`,
          values: shownColumns.map(({ index: columnIndex }) => rowValues[columnIndex]),
          barLabels: shownColumns.map(
            ({ name: columnName, index: columnIndex }) => columnName || `Class ${columnIndex + 1}`
          ),
          chartHighlighted: false,
          barHighlighted: (barIndex: number) =>
            highlightedCategoryGroupKey !== null &&
            categoryGroupKeyForLabel(shownColumns[barIndex].name) === highlightedCategoryGroupKey,
        }
      })

  if (rows.length === 0 || columns.length === 0) {
    return (
      <p className="model-detail-panel__empty mb-0 px-2 py-2">
        Empty table — add rows and columns to chart values.
      </p>
    )
  }

  if (shownRows.length === 0 || shownColumns.length === 0) {
    const missing =
      shownRows.length === 0 && shownColumns.length === 0
        ? "fields or classes"
        : shownRows.length === 0
          ? "fields"
          : "classes"
    return (
      <p className="model-detail-panel__empty mb-0 px-2 py-2">
        No {missing} selected — choose {missing} from the dropdown to show charts.
      </p>
    )
  }

  const scale = sharedChartScale(charts.map(chart => chart.values))
  const axisTicks = chartAxisTicks(scale.minVal, scale.maxVal)

  return (
    <div className="model-detail-panel__row-charts">
      {charts.map((chart, shownIndex) => {
        const isFirstChart = shownIndex === 0

        return (
          <section key={chart.key} className="model-detail-panel__row-chart">
            <h6
              className={`model-detail-panel__row-chart-title${
                chart.chartHighlighted ? " model-detail-panel__row-chart-title--highlighted" : ""
              }`}
              title={chart.title}
            >
              {chart.title}
            </h6>
            {isFirstChart && (
              <div className="model-detail-panel__row-chart-bar-row model-detail-panel__row-chart-axis">
                <span />
                <div className="model-detail-panel__row-chart-axis-track">
                  {axisTicks.map((tick, tickIndex) => {
                    const leftPct = ((tick - scale.minVal) / scale.span) * 100
                    const edge =
                      tickIndex === 0 ? "start" : tickIndex === axisTicks.length - 1 ? "end" : "mid"
                    return (
                      <div
                        key={`axis-tick-${tick}`}
                        className={`model-detail-panel__row-chart-axis-tick model-detail-panel__row-chart-axis-tick--${edge}`}
                        style={{ left: `${leftPct}%` }}
                      >
                        <span className="model-detail-panel__row-chart-axis-label">
                          {formatTableChartValue(tick)}
                        </span>
                        <span className="model-detail-panel__row-chart-axis-mark" />
                      </div>
                    )
                  })}
                </div>
                <span />
              </div>
            )}
            {chart.barLabels.map((barLabel, barIndex) => {
              const value = chart.values[barIndex]
              const highlighted = chart.barHighlighted(barIndex)
              const bar = value === null ? null : rowChartBarLayout(value, scale)
              const barColor = value === null ? null : chartBarColor(value, scale)

              return (
                <div
                  key={`${chart.key}-bar-${barIndex}`}
                  className={`model-detail-panel__row-chart-bar-row${
                    highlighted ? " model-detail-panel__row-chart-bar-row--highlighted" : ""
                  }`}
                >
                  <span className="model-detail-panel__row-chart-col" title={barLabel}>
                    {barLabel || `${isClass ? "Field" : "Class"} ${barIndex + 1}`}
                  </span>
                  <div className="model-detail-panel__row-chart-track">
                    <div
                      className="model-detail-panel__row-chart-zero"
                      style={{ left: `${scale.zeroPct}%` }}
                    />
                    {bar && barColor && value !== null && value !== 0 && (
                      <div
                        className={`model-detail-panel__row-chart-bar${
                          bar.negative ? " model-detail-panel__row-chart-bar--negative" : " model-detail-panel__row-chart-bar--positive"
                        }${barColor.darkText ? " model-detail-panel__row-chart-bar--dark" : ""}`}
                        style={{
                          left: `${bar.leftPct}%`,
                          width: `${bar.widthPct}%`,
                          backgroundColor: barColor.color,
                        }}
                        title={`${barLabel}: ${formatTableChartValue(value)}`}
                      />
                    )}
                  </div>
                  <span className="model-detail-panel__row-chart-value">
                    {value === null ? "—" : formatTableChartValue(value)}
                  </span>
                </div>
              )
            })}
          </section>
        )
      })}
    </div>
  )
}

type TableSpreadsheetActionsProps = {
  entry: TableDetailEntry
  filename: string
  onImport: (data: TableSpreadsheetData) => void
  className?: string
}

const TableSpreadsheetActions = ({
  entry,
  filename,
  onImport,
  className,
}: TableSpreadsheetActionsProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleImportFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const isExcel = /\.xlsx?$/i.test(file.name)
    if (isExcel) {
      const reader = new FileReader()
      reader.onload = () => {
        const buffer = reader.result
        if (!(buffer instanceof ArrayBuffer)) return
        const parsed = parseTableSpreadsheetXlsx(buffer)
        if (parsed) onImport(parsed)
        if (fileInputRef.current) fileInputRef.current.value = ""
      }
      reader.readAsArrayBuffer(file)
    } else {
      const reader = new FileReader()
      reader.onload = () => {
        const parsed = parseTableSpreadsheetCsv(String(reader.result ?? ""))
        if (parsed) onImport(parsed)
        if (fileInputRef.current) fileInputRef.current.value = ""
      }
      reader.readAsText(file)
    }
  }

  return (
    <div
      className={`model-detail-panel__matrix-csv-actions d-flex align-items-center flex-wrap${className ? ` ${className}` : ""}`}
    >
      <button
        type="button"
        className="btn btn-sm btn-outline-primary model-detail-panel__matrix-csv-btn"
        onClick={() => fileInputRef.current?.click()}
      >
        <i className="fa fa-upload mr-1" aria-hidden="true" />
        Import
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary model-detail-panel__matrix-csv-btn ml-1"
        onClick={() =>
          downloadTableSpreadsheetCsv(entry.rows, entry.columns, entry.cells, filename)
        }
      >
        <i className="fa fa-download mr-1" aria-hidden="true" />
        CSV
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-success model-detail-panel__matrix-csv-btn ml-1"
        onClick={() =>
          downloadTableSpreadsheetXlsx(entry.rows, entry.columns, entry.cells, filename)
        }
      >
        <i className="fa fa-download mr-1" aria-hidden="true" />
        XLSX
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
        className="model-detail-panel__matrix-csv-file"
        onChange={handleImportFile}
      />
    </div>
  )
}

type TableDetailExpandedDialogProps = {
  open: boolean
  entry: TableDetailEntry
  onClose: () => void
  onImport: (data: TableSpreadsheetData) => void
  onCellChange: (rowIndex: number, columnIndex: number, value: string) => void
  onCellBlur: (rowIndex: number, columnIndex: number, value: string) => void
  onRowNameChange: (index: number, name: string) => void
  onRowNameBlur: (index: number, name: string) => void
  onColumnNameChange: (index: number, name: string) => void
  onColumnNameBlur: (index: number, name: string) => void
  highlightedCategoryGroupKey?: string | null
}

const TableDetailExpandedDialog = ({
  open,
  entry,
  onClose,
  onImport,
  onCellChange,
  onCellBlur,
  onRowNameChange,
  onRowNameBlur,
  onColumnNameChange,
  onColumnNameBlur,
  highlightedCategoryGroupKey = null,
}: TableDetailExpandedDialogProps) => {
  const dialogTitleId = `tableExpandedTitle-${entry.nodeId}`
  const [viewMode, setViewMode] = React.useState<"table" | "chart">("table")
  const [chartGroupBy, setChartGroupBy] = React.useState<ChartGroupBy>("field")
  const [selectedRowIndices, setSelectedRowIndices] = React.useState<Set<number>>(
    () => new Set(entry.rows.map((_, index) => index))
  )
  const [selectedColumnIndices, setSelectedColumnIndices] = React.useState<Set<number>>(
    () => new Set(entry.columns.map((_, index) => index))
  )
  const previousRowCountRef = React.useRef(entry.rows.length)
  const previousColumnCountRef = React.useRef(entry.columns.length)

  React.useEffect(() => {
    if (!open) {
      setViewMode("table")
      setChartGroupBy("field")
      return
    }
    setSelectedRowIndices(new Set(entry.rows.map((_, index) => index)))
    setSelectedColumnIndices(new Set(entry.columns.map((_, index) => index)))
    previousRowCountRef.current = entry.rows.length
    previousColumnCountRef.current = entry.columns.length
  }, [open, entry.nodeId])

  React.useEffect(() => {
    const rowCount = entry.rows.length
    if (previousRowCountRef.current === rowCount) return
    setSelectedRowIndices(previous => {
      const next = new Set<number>()
      const previousCount = previousRowCountRef.current
      for (let index = 0; index < rowCount; index++) {
        if (previous.has(index) || index >= previousCount) next.add(index)
      }
      previousRowCountRef.current = rowCount
      return next
    })
  }, [entry.rows.length])

  React.useEffect(() => {
    const columnCount = entry.columns.length
    if (previousColumnCountRef.current === columnCount) return
    setSelectedColumnIndices(previous => {
      const next = new Set<number>()
      const previousCount = previousColumnCountRef.current
      for (let index = 0; index < columnCount; index++) {
        if (previous.has(index) || index >= previousCount) next.add(index)
      }
      previousColumnCountRef.current = columnCount
      return next
    })
  }, [entry.columns.length])

  React.useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      className="modal d-block model-detail-panel__matrix-modal model-detail-panel__matrix-modal--table"
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogTitleId}
      style={{ backgroundColor: "rgba(0, 0, 0, 0.45)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered model-detail-panel__matrix-modal-dialog"
        role="document"
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header py-2 d-flex align-items-center flex-wrap">
            <h5 className="modal-title mb-0" id={dialogTitleId}>
              {entry.name}
            </h5>
            <div className="btn-group btn-group-sm ml-3 model-detail-panel__view-toggle" role="group" aria-label="Table view">
              <button
                type="button"
                className={`btn ${viewMode === "table" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setViewMode("table")}
              >
                Table
              </button>
              <button
                type="button"
                className={`btn ${viewMode === "chart" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setViewMode("chart")}
              >
                Chart
              </button>
            </div>
            {viewMode === "chart" && (
              <div className="btn-group btn-group-sm ml-3 model-detail-panel__view-toggle" role="group" aria-label="Chart grouping">
                <button
                  type="button"
                  className={`btn ${chartGroupBy === "field" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setChartGroupBy("field")}
                >
                  Field
                </button>
                <button
                  type="button"
                  className={`btn ${chartGroupBy === "class" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setChartGroupBy("class")}
                >
                  Class
                </button>
              </div>
            )}
            <TableDetailVisibilityFilter
              fields={entry.rows}
              classes={entry.columns}
              selectedFieldIndices={selectedRowIndices}
              selectedClassIndices={selectedColumnIndices}
              onSelectedFieldIndicesChange={setSelectedRowIndices}
              onSelectedClassIndicesChange={setSelectedColumnIndices}
            />
            <TableSpreadsheetActions
              entry={entry}
              filename={tableExportFilename(entry)}
              onImport={onImport}
              className="ml-3"
            />
            <button type="button" className="close ml-auto" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body model-detail-panel__matrix-modal-body">
            {viewMode === "chart" ? (
              <TableDetailChartView
                entry={entry}
                highlightedCategoryGroupKey={highlightedCategoryGroupKey}
                groupBy={chartGroupBy}
                visibleRowIndices={selectedRowIndices}
                visibleColumnIndices={selectedColumnIndices}
              />
            ) : (
            <TableDetailTable
              entry={entry}
              size="expanded"
              onCellChange={onCellChange}
              onCellBlur={onCellBlur}
              onRowNameChange={onRowNameChange}
              onRowNameBlur={onRowNameBlur}
              onColumnNameChange={onColumnNameChange}
              onColumnNameBlur={onColumnNameBlur}
              highlightedCategoryGroupKey={highlightedCategoryGroupKey}
              visibleRowIndices={selectedRowIndices}
              visibleColumnIndices={selectedColumnIndices}
            />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

const TableDetailSection = ({
  entry,
  model,
  modelEditorApi,
  onCellChange,
  onCellBlur,
  onRowNameChange,
  onRowNameBlur,
  onColumnNameChange,
  onColumnNameBlur,
  highlightedCategoryGroupKey = null,
}: TableDetailSectionProps) => {
  const [tableExpanded, setTableExpanded] = React.useState(false)
  const liveEntry = React.useMemo(
    () => getTableDetailEntry(model, entry.nodeId) ?? entry,
    [model, entry]
  )
  const columnHighlightScrollRef = React.useRef<HTMLTableCellElement>(null)

  const hasColumnHighlight =
    highlightedCategoryGroupKey !== null &&
    liveEntry.columns.some(
      column => categoryGroupKeyForLabel(column) === highlightedCategoryGroupKey
    )

  React.useEffect(() => {
    if (!hasColumnHighlight) return
    columnHighlightScrollRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, [highlightedCategoryGroupKey, liveEntry, hasColumnHighlight])

  const handleImport = React.useCallback(
    (data: TableSpreadsheetData) => {
      modelEditorApi?.importTableData(entry.nodeId, data)
    },
    [entry.nodeId, modelEditorApi]
  )

  return (
    <>
      <div className="border-bottom">
        <div className="model-detail-panel__header text-dark bg-light border-bottom d-flex align-items-center">
          <span className="text-truncate flex-grow-1" title={liveEntry.name}>
            {liveEntry.name}
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-secondary model-detail-panel__matrix-expand-btn flex-shrink-0"
            onClick={() => setTableExpanded(true)}
          >
            <i className="fas fa-expand-alt mr-1" aria-hidden="true" />
            Expand table
          </button>
        </div>
        <TableDetailTable
          entry={liveEntry}
          size="compact"
          onCellChange={onCellChange}
          onCellBlur={onCellBlur}
          onRowNameChange={onRowNameChange}
          onRowNameBlur={onRowNameBlur}
          onColumnNameChange={onColumnNameChange}
          onColumnNameBlur={onColumnNameBlur}
          highlightedCategoryGroupKey={highlightedCategoryGroupKey}
          columnHighlightScrollRef={columnHighlightScrollRef}
        />
      </div>
      <TableDetailExpandedDialog
        open={tableExpanded}
        entry={liveEntry}
        onClose={() => setTableExpanded(false)}
        onImport={handleImport}
        onCellChange={onCellChange}
        onCellBlur={onCellBlur}
        onRowNameChange={onRowNameChange}
        onRowNameBlur={onRowNameBlur}
        onColumnNameChange={onColumnNameChange}
        onColumnNameBlur={onColumnNameBlur}
        highlightedCategoryGroupKey={highlightedCategoryGroupKey}
      />
    </>
  )
}

type TableDetailHeaderInputProps = {
  value: string
  compact?: boolean
  expanded?: boolean
  ariaLabel: string
  onChange: (value: string) => void
  onBlur: (value: string) => void
}

const TableDetailHeaderInput = ({
  value: initialValue,
  compact,
  expanded,
  ariaLabel,
  onChange,
  onBlur,
}: TableDetailHeaderInputProps) => {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  return (
    <input
      type="text"
      className={`form-control form-control-sm model-detail-panel__input model-detail-panel__input--header${compact ? " model-detail-panel__input--matrix" : ""}${expanded ? " model-detail-panel__input--matrix-expanded" : ""}`}
      value={value}
      aria-label={ariaLabel}
      onChange={e => {
        setValue(e.target.value)
        onChange(e.target.value)
      }}
      onBlur={e => onBlur(e.target.value)}
    />
  )
}

type TableDetailCellProps = {
  rowLabel: string
  columnLabel: string
  value: string
  expanded?: boolean
  onValueChange: (value: string) => void
  onValueBlur: (value: string) => void
}

const TableDetailCell = ({
  rowLabel,
  columnLabel,
  value: initialValue,
  expanded,
  onValueChange,
  onValueBlur,
}: TableDetailCellProps) => {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue, rowLabel, columnLabel])

  return (
    <DetailNumericInput
      value={value}
      compact={!expanded}
      expanded={expanded}
      ariaLabel={`Cell ${rowLabel}, ${columnLabel}`}
      onChange={next => {
        setValue(next)
        onValueChange(next)
      }}
      onBlur={next => onValueBlur(next)}
    />
  )
}

type NumericConstantDetailRowProps = {
  entry: NumericConstantDetailEntry
  onValueChange: (value: string) => void
  onValueBlur: (value: string) => void
}

const NumericConstantDetailRow = ({ entry, onValueChange, onValueBlur }: NumericConstantDetailRowProps) => {
  const [value, setValue] = React.useState(entry.value)

  React.useEffect(() => {
    setValue(entry.value)
  }, [entry.value, entry.nodeId])

  return (
    <div className="model-detail-panel__row model-detail-panel__row--numeric text-dark font-weight-medium">
      <span className="model-detail-panel__label text-truncate" title={entry.name}>
        {entry.name}
      </span>
      <DetailNumericInput
        value={value}
        ariaLabel={`Value for ${entry.name}`}
        onChange={next => {
          setValue(next)
          onValueChange(next)
        }}
        onBlur={next => onValueBlur(next)}
      />
    </div>
  )
}

type CategoricalToNumericDetailSectionProps = {
  entry: CategoricalToNumericDetailEntry
  highlightedCategoryGroupKey: string | null
  highlightScrollRef: React.RefObject<HTMLElement | null>
  onMapValueChange: (categoryValue: number, numericValue: string) => void
  onMapValueBlur: (categoryValue: number, numericValue: string) => void
}

const CategoricalToNumericDetailSection = ({
  entry,
  highlightedCategoryGroupKey,
  highlightScrollRef,
  onMapValueChange,
  onMapValueBlur,
}: CategoricalToNumericDetailSectionProps) => (
  <div className="border-bottom">
    <div className="model-detail-panel__header text-dark bg-light border-bottom">{entry.name}</div>
    {entry.rows.map(row => {
      const isHighlighted =
        highlightedCategoryGroupKey !== null &&
        categoryGroupKeyForLabel(row.label) === highlightedCategoryGroupKey
      return (
        <CategoricalMapDetailRowView
          key={`${entry.nodeId}-${row.categoryValue}`}
          row={row}
          highlighted={isHighlighted}
          rowRef={isHighlighted ? highlightScrollRef : undefined}
          onValueChange={numericValue => onMapValueChange(row.categoryValue, numericValue)}
          onValueBlur={numericValue => onMapValueBlur(row.categoryValue, numericValue)}
        />
      )
    })}
  </div>
)

type CategoricalDetailMatrixTableSize = "compact" | "expanded"

type CategoricalDetailMatrixTableProps = {
  matrix: CategoricalDetailMatrix
  size?: CategoricalDetailMatrixTableSize
  highlightedCategoryGroupKey: string | null
  highlightScrollRef?: React.RefObject<HTMLElement | null>
  onMapValueChange: (nodeId: number, categoryValue: number, numericValue: string) => void
  onMapValueBlur: (nodeId: number, categoryValue: number, numericValue: string) => void
}

const CategoricalDetailMatrixTable = ({
  matrix,
  size = "compact",
  highlightedCategoryGroupKey,
  highlightScrollRef,
  onMapValueChange,
  onMapValueBlur,
}: CategoricalDetailMatrixTableProps) => {
  const isExpandedLayout = size === "expanded"

  return (
  <div
    className={`model-detail-panel__matrix-wrap${isExpandedLayout ? " model-detail-panel__matrix-wrap--expanded" : ""}`}
  >
    <table
      className={`model-detail-panel__matrix${isExpandedLayout ? " model-detail-panel__matrix--expanded" : ""}`}
    >
      <colgroup>
        <col className="model-detail-panel__matrix-col-label" />
        {matrix.columns.map(col => (
          <col key={col.key} className="model-detail-panel__matrix-col-value" />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th className="model-detail-panel__matrix-corner" aria-hidden="true" />
          {matrix.columns.map(col => {
            const isLinked = col.nodeIds.length > 1
            const title = isLinked
              ? `${col.componentName} (linked across ${col.nodeIds.length} components — editing updates them all)`
              : col.componentName
            return (
              <th
                key={col.key}
                className="model-detail-panel__matrix-col-head"
                title={title}
              >
                <span className="model-detail-panel__matrix-col-head-text">
                  {col.componentName}
                  {isLinked && (
                    <i
                      className="fas fa-link ml-1 model-detail-panel__matrix-col-head-link-icon"
                      aria-hidden="true"
                    />
                  )}
                </span>
              </th>
            )
          })}
        </tr>
      </thead>
      <tbody>
        {matrix.rows.map(row => {
          const isHighlighted =
            highlightedCategoryGroupKey !== null && row.groupKey === highlightedCategoryGroupKey
          return (
          <tr
            key={row.groupKey}
            ref={isHighlighted ? highlightScrollRef as React.Ref<HTMLTableRowElement> : undefined}
            className={isHighlighted ? "model-detail-panel__matrix-row--highlighted" : undefined}
          >
            <th
              className="model-detail-panel__matrix-row-head"
              title={
                row.siblingNodeId !== undefined
                  ? `${row.label} — component #${row.siblingNodeId}`
                  : row.label
              }
              scope="row"
            >
              {row.label}
              {row.siblingNodeId !== undefined && (
                <span
                  className="model-detail-panel__matrix-row-head-sub text-muted"
                  aria-label={`Component #${row.siblingNodeId}`}
                >
                  #{row.siblingNodeId}
                </span>
              )}
            </th>
            {row.cells.map((cell, colIndex) => {
              const col = matrix.columns[colIndex]
              return (
                <td
                  key={`${row.groupKey}-${col.key}`}
                  className="model-detail-panel__matrix-cell"
                >
                  <div className="model-detail-panel__matrix-cell-inner">
                    {cell ? (
                      <CategoricalDetailMatrixCell
                        cell={cell}
                        categoryLabel={row.label}
                        size={size}
                        onValueChange={numericValue => {
                          // Fan the write out across every sibling node merged
                          // into this column. The model_view handler refuses
                          // writes against socket-connected inputs internally,
                          // so it's safe to call for all targets — connected
                          // ones simply ignore the new value.
                          for (const target of cell.targets) {
                            onMapValueChange(target.nodeId, target.categoryValue, numericValue)
                          }
                        }}
                        onValueBlur={numericValue => {
                          for (const target of cell.targets) {
                            onMapValueBlur(target.nodeId, target.categoryValue, numericValue)
                          }
                        }}
                      />
                    ) : (
                      <span className="model-detail-panel__matrix-empty" aria-hidden="true">
                        —
                      </span>
                    )}
                  </div>
                </td>
              )
            })}
          </tr>
          )
        })}
      </tbody>
    </table>
  </div>
  )
}

type CategoricalDetailMatrixCsvActionsProps = {
  matrix: CategoricalDetailMatrix
  onImport: (csvText: string) => void
  className?: string
}

const CategoricalDetailMatrixCsvActions = ({
  matrix,
  onImport,
  className,
}: CategoricalDetailMatrixCsvActionsProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleImportCsv = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0]
    if (!file) {
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      onImport(String(reader.result || ""))
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
    reader.readAsText(file)
  }

  return (
    <div className={`model-detail-panel__matrix-csv-actions d-flex align-items-center${className ? ` ${className}` : ""}`}>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary model-detail-panel__matrix-csv-btn"
        onClick={() => fileInputRef.current?.click()}
      >
        <i className="fa fa-upload mr-1" aria-hidden="true" />
        Import CSV
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary model-detail-panel__matrix-csv-btn ml-1"
        onClick={() => downloadCategoricalDetailMatrixCsv(matrix, "category-values")}
      >
        <i className="fa fa-download mr-1" aria-hidden="true" />
        Export CSV
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        className="model-detail-panel__matrix-csv-file"
        onChange={handleImportCsv}
      />
    </div>
  )
}

type CategoricalDetailMatrixExpandedDialogProps = {
  open: boolean
  matrix: CategoricalDetailMatrix
  highlightedCategoryGroupKey: string | null
  highlightScrollRef: React.RefObject<HTMLElement | null>
  onClose: () => void
  onImport: (csvText: string) => void
  onMapValueChange: (nodeId: number, categoryValue: number, numericValue: string) => void
  onMapValueBlur: (nodeId: number, categoryValue: number, numericValue: string) => void
}

const CategoricalDetailMatrixExpandedDialog = ({
  open,
  matrix,
  highlightedCategoryGroupKey,
  highlightScrollRef,
  onClose,
  onImport,
  onMapValueChange,
  onMapValueBlur,
}: CategoricalDetailMatrixExpandedDialogProps) => {
  if (!open) {
    return null
  }

  return createPortal(
    <div
      className="modal d-block model-detail-panel__matrix-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="categoryMatrixExpandedTitle"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.45)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered model-detail-panel__matrix-modal-dialog"
        role="document"
        onClick={event => event.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header py-2 d-flex align-items-center">
            <h5 className="modal-title mb-0" id="categoryMatrixExpandedTitle">
              Category values
            </h5>
            <CategoricalDetailMatrixCsvActions
              matrix={matrix}
              onImport={onImport}
              className="ml-3"
            />
            <button type="button" className="close ml-auto" aria-label="Close" onClick={onClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body model-detail-panel__matrix-modal-body">
            <CategoricalDetailMatrixTable
              matrix={matrix}
              size="expanded"
              highlightedCategoryGroupKey={highlightedCategoryGroupKey}
              highlightScrollRef={highlightScrollRef}
              onMapValueChange={onMapValueChange}
              onMapValueBlur={onMapValueBlur}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

type CategoricalDetailMatrixCellProps = {
  cell: CategoricalDetailComponentRow
  categoryLabel: string
  size?: CategoricalDetailMatrixTableSize
  onValueChange: (numericValue: string) => void
  onValueBlur: (numericValue: string) => void
}

const CategoricalDetailMatrixCell = ({
  cell,
  categoryLabel,
  size = "compact",
  onValueChange,
  onValueBlur,
}: CategoricalDetailMatrixCellProps) => {
  const [value, setValue] = React.useState(cell.numericValue)

  React.useEffect(() => {
    setValue(cell.numericValue)
  }, [cell.numericValue, cell.nodeId, cell.categoryValue])

  const isLinked = cell.targets.length > 1
  const linkedTitle = isLinked
    ? cell.valuesAreMixed
      ? `Linked across ${cell.targets.length} components — current values differ (${cell.targets
          .map(t => t.numericValue || "—")
          .join(", ")}). Editing will overwrite all of them.`
      : `Linked across ${cell.targets.length} components — editing updates all.`
    : undefined

  // If any sibling is socket-connected we disable editing for the whole
  // merged cell — editing would silently desync the wired sibling from the
  // others. The tooltip distinguishes the single-component case from the
  // linked case so the user knows where to look.
  const title = cell.socketConnected
    ? isLinked
      ? "Editing disabled — one of the linked components has this value driven by a connected input."
      : "Value is driven by a connected input"
    : linkedTitle

  return (
    <DetailNumericInput
      value={value}
      disabled={cell.socketConnected}
      compact={size === "compact"}
      expanded={size === "expanded"}
      ariaLabel={`${categoryLabel} — ${cell.componentName}`}
      title={title}
      mixed={isLinked && cell.valuesAreMixed}
      onChange={next => {
        if (cell.socketConnected) return
        setValue(next)
        onValueChange(next)
      }}
      onBlur={next => {
        if (cell.socketConnected) return
        onValueBlur(next)
      }}
    />
  )
}

type CategoricalMapDetailRowProps = {
  row: CategoricalMapDetailRow
  highlighted?: boolean
  rowRef?: React.RefObject<HTMLElement | null>
  onValueChange: (numericValue: string) => void
  onValueBlur: (numericValue: string) => void
}

const CategoricalMapDetailRowView = ({
  row,
  highlighted,
  rowRef,
  onValueChange,
  onValueBlur,
}: CategoricalMapDetailRowProps) => {
  const [value, setValue] = React.useState(row.numericValue)

  React.useEffect(() => {
    setValue(row.numericValue)
  }, [row.numericValue, row.categoryValue])

  return (
    <div
      ref={rowRef as React.Ref<HTMLDivElement> | undefined}
      className={`model-detail-panel__row text-dark${highlighted ? " model-detail-panel__row--highlighted" : ""}`}
    >
      <span className="model-detail-panel__label text-truncate" title={row.label}>
        {row.label}
      </span>
      <DetailNumericInput
        value={value}
        disabled={row.socketConnected}
        ariaLabel={`Numeric value for ${row.label}`}
        title={row.socketConnected ? "Value is driven by a connected input" : undefined}
        onChange={next => {
          setValue(next)
          onValueChange(next)
        }}
        onBlur={next => onValueBlur(next)}
      />
    </div>
  )
}

type DetailNumericInputProps = {
  value: string
  disabled?: boolean
  compact?: boolean
  expanded?: boolean
  ariaLabel: string
  title?: string
  /** When true, marks the input visually to flag "linked cells with currently mixed values". */
  mixed?: boolean
  onChange: (value: string) => void
  onBlur: (value: string) => void
}

const DetailNumericInput = ({
  value,
  disabled,
  compact,
  expanded,
  ariaLabel,
  title,
  mixed,
  onChange,
  onBlur,
}: DetailNumericInputProps) => (
  <input
    type="number"
    className={`form-control form-control-sm model-detail-panel__input${compact ? " model-detail-panel__input--matrix" : ""}${expanded ? " model-detail-panel__input--matrix-expanded" : ""}${disabled ? " model-detail-panel__input--disabled" : ""}${mixed ? " model-detail-panel__input--mixed" : ""}`}
    value={value}
    disabled={disabled}
    aria-label={ariaLabel}
    title={title}
    onChange={e => {
      if (disabled) return
      onChange(e.target.value)
    }}
    onBlur={e => {
      if (disabled) return
      onBlur(e.target.value)
    }}
  />
)
